<script>
	import client from "src/services/client";
	import { activeTableHasError, loading } from "src/store/store";
	import { extractErrorMessage } from "src/utils/misc";
	import { onDestroy, onMount } from "svelte";
	import CustomButton from "../CustomButton/CustomButton.svelte";
	import CustomInput from "../CustomInput/CustomInput.svelte";
	import CustomSearch from "../CustomSearch/CustomSearch.svelte";
	import LoaderContainer from "../Loader/LoaderContainer.svelte";

	export let tableData;
	export let updateTableData;
	export let columnData;
	export let checkTableContent = false;
	export let changeCheckTable;

	let priorityOptions = [];
	let copyRowIdx = -1;

	// Function to add a new row
	const addRow = () => {
		const updatedTableData = [
			...tableData,
			...Array(5)
				.fill()
				.map(() => ({ ...columnData })),
		];
		updateTableData(updatedTableData);
		tableData = updatedTableData;
	};

	// Function to delete a row
	const deleteRow = (index) => {
		const updatedTableData = tableData.filter(
			(_, rowIndex) => rowIndex !== index
		);
		updateTableData(updatedTableData);
		tableData = updatedTableData;
	};

	const focusNextInput = (event, rowIndex, colIndex) => {
		// Prevent the browser's default Tab behavior
		if (event.key === "Tab") {
			event.preventDefault();

			// Calculate the next focusable input
			const totalColumns = Object.keys(columnData).length;
			const totalRows = tableData.length;

			let nextRow = rowIndex;
			let nextCol = colIndex + 1;

			if (nextCol >= totalColumns) {
				nextCol = 0; // Wrap to the first column
				nextRow = rowIndex + 1;
			}

			if (nextRow < totalRows) {
				const nextInputId = `input-${nextRow}-${nextCol}`;
				const nextInput = document.getElementById(nextInputId);
				if (nextInput) {
					nextInput.focus();
				}
			}
		}
	};

	$: handleDropdownError = (value) => {
		if (value === -1) {
			return "2px solid red !important";
		} else {
			return "";
		}
	};

	$: handleSearchError = (value) => {
		if (value === undefined) {
			activeTableHasError.set(true);
			return "2px solid red !important";
		} else {
			return "";
		}
	};

	let presetTasks = [];

	async function fetchPresetTasks() {
		try {
			loading.set(true);

			// fetch active preset tasks
			const res = await client("/v1/presetTasks/search", {
				method: "POST",
				version: "Tasking",
				data: { isActive: true },
			});

			presetTasks = res.data.data.map((task) => {
				return {
					value: task.id,
					label: task.description,
				};
			});

			loading.set(false);
		} catch (err) {
			loading.set(false);
			console.log(err);
		}
	}

	onMount(async () => {
		let lookups = await client("/v1/Tasks/lookups", {
			method: "GET",
			version: "Tasking",
		});
		fetchPresetTasks(); // fetch only active preset tasks
		priorityOptions = lookups.data.taskPriorities.map((priority) => {
			return {
				value: priority.id.toString(),
				label: priority.name,
			};
		});
	});

	async function handleDebounceSearch(e) {
		if (e !== "") {
			try {
				let carRes = await client(`/v1/plateNumbers/${e}?limit=100`, {
					method: "GET",
					version: "Vehicle",
				});
				let carOptions = carRes.data.map((car) => {
					return {
						value: car.vehicleId,
						label: car.plateNo,
					};
				});
				return carOptions;
			} catch (error) {
				console.log(error);
				extractErrorMessage(error);
				return [];
			}
		}
	}

	// filter tasks based on input for preset tasks
	function handleTaskSearch(input) {
		// returns all preset tasks
		if (!input) return Promise.resolve(presetTasks);
		return Promise.resolve(
			presetTasks.filter((task) =>
				task.label.toLowerCase().includes(input.toLowerCase())
			)
		);
	}

	// filter priorities based on input
	function handlePrioritySearch(input) {
		if (!input) return Promise.resolve(priorityOptions);
		return Promise.resolve(
			priorityOptions.filter((priority) =>
				priority.label.toLowerCase().includes(input.toLowerCase())
			)
		);
	}

	function handlePasteEvent(event) {
		event.preventDefault();
		navigator.clipboard.readText().then((clipText) => {});
	}

	window.addEventListener("paste", handlePasteEvent);
	window.addEventListener("keydown", handleKeyDownPaste);

	async function handleKeyDownPaste(event) {
		// function to paste data from clipboard
		// if copyRowIdx is set, it will paste data starting from that index
		if (
			(event.ctrlKey || event.metaKey) &&
			event.key.toLowerCase() === "v"
		) {
			loading.set(true);
			const clipText = await navigator.clipboard.readText();
			const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
			const isWindows =
				navigator.platform.toUpperCase().indexOf("WIN") >= 0;
			let rows;
			if (isMac) {
				rows = clipText
					.trim()
					.split("\n")
					.map((line) => line.split("\t"));
			} else if (isWindows) {
				rows = clipText
					.trim()
					.split("\r\n")
					.map((line) => line.split("\t"));
			}

			const newTableData = [...tableData];

			// Store parsed row data for reuse
			const parsedRows = [];

			for (let i = 0; i < rows.length; i++) {
				const cols = rows[i];
				let vehicleData;
				let taskOption;
				let priorityOption;
				if (cols[0]) {
					try {
						let carRes = await client(
							`/v1/plateNumbers/${cols[0]}?limit=100`,
							{
								method: "GET",
								version: "Vehicle",
							}
						);
						vehicleData = {
							value: carRes.data[0]?.vehicleId,
							label: cols[0],
						};
					} catch (e) {
						vehicleData = undefined;
					}
				}
				if (cols[1]) {
					taskOption = presetTasks.find(
						(task) =>
							task.label === cols[1] || task.value == cols[1]
					);
				}
				if (cols[2]) {
					priorityOption =
						priorityOptions.find(
							(priority) => priority.label == cols[2]
						) || -1;
				}
				parsedRows.push({
					vehicleNo: cols[0] ? vehicleData : undefined,
					Task: cols[1] ? taskOption : -1,
					Priority: cols[2] ? priorityOption : -1,
					Remarks: cols[3],
				});
			}
			// If copyRowIdx is set, start copying from that index
			let startIdx = copyRowIdx !== -1 ? copyRowIdx : 0;

			// If pasted data exceeds table, add more rows
			const requiredRows = startIdx + parsedRows.length;
			if (requiredRows > newTableData.length) {
				const rowsToAdd = requiredRows - newTableData.length;
				for (let i = 0; i < rowsToAdd; i++) {
					newTableData.push({ ...columnData });
				}
			}

			for (let i = 0; i < parsedRows.length; i++) {
				const targetIdx = startIdx + i;
				if (targetIdx < newTableData.length) {
					newTableData[targetIdx] = parsedRows[i];
				}
			}
			copyRowIdx = -1;
			updateTableData(newTableData);
			loading.set(false);
		}
	}

	onDestroy(() => {
		window.removeEventListener("paste", handlePasteEvent);
		window.removeEventListener("keydown", handleKeyDownPaste);
	});
</script>

<LoaderContainer>
	<div>
		<table>
			<thead>
				<tr>
					<th>Vehicle No.</th>
					<th>Task</th>
					<th>Priority</th>
					<th>Remarks</th>
					<th>Action</th>
				</tr>
			</thead>
			<tbody>
				{#each tableData as row, rowIndex}
					<tr>
						{#each Object.keys(columnData) as colKey, colIndex}
							{#if colKey === "Task"}
								<td
									style={`border: ${handleDropdownError(
										tableData[rowIndex][colKey]
									)}`}
									class="med-td"
								>
									<CustomSearch
										items={presetTasks}
										loadOptions={handleTaskSearch}
										type="text"
										placeholder="Select a task"
										bind:value={tableData[rowIndex][colKey]}
										containerClasses="search-border"
										isClearable={false}
										chevron={true}
										handleClear={() => {
											tableData[rowIndex][colKey] =
												undefined;
											activeTableHasError.set(true);
										}}
										onSelect={() => {
											const cellElement =
												document.getElementById(
													`input-${rowIndex}-${colIndex}`
												);
											if (cellElement)
												cellElement.style.border = "";
										}}
									/>
								</td>
							{:else if colKey === "Priority"}
								<td
									style={`border: ${handleDropdownError(
										tableData[rowIndex][colKey]
									)}`}
									class="sm-td"
								>
									<CustomSearch
										items={priorityOptions}
										loadOptions={handlePrioritySearch}
										type="text"
										placeholder="Select a priority"
										bind:value={tableData[rowIndex][colKey]}
										containerClasses="search-border"
										isClearable={false}
										chevron={true}
										handleClear={() => {
											tableData[rowIndex][colKey] =
												undefined;
											activeTableHasError.set(true);
										}}
										onSelect={() => {
											const cellElement =
												document.getElementById(
													`input-${rowIndex}-${colIndex}`
												);
											if (cellElement)
												cellElement.style.border = "";
										}}
									/>
								</td>
							{:else if colKey === "vehicleNo"}
								<td
									style={`border: ${handleSearchError(
										tableData[rowIndex][colKey]
									)}`}
									class="sm-td"
									on:click={() => {
										copyRowIdx = rowIndex;
									}}
									on:keydown={(e) => {
										if (
											e.key === "Enter" ||
											e.key === " "
										) {
											copyRowIdx = rowIndex;
										}
									}}
								>
									<CustomSearch
										loadOptions={handleDebounceSearch}
										type="text"
										placeholder={`Enter vehicle no.`}
										bind:value={tableData[rowIndex][colKey]}
										on:keydown={(e) => {
											focusNextInput(
												e,
												rowIndex,
												colIndex
											);
										}}
										containerClasses={`search-border`}
										handleClear={() =>
											activeTableHasError.set(true)}
										onSelect={(e) => {
											const cellElement =
												document.getElementById(
													`input-${rowIndex}-${colIndex}`
												);
											if (cellElement) {
												cellElement.style.border = "";
											}
										}}
									/>
								</td>
							{:else}
								<td class="med-td">
									<CustomInput
										type="text"
										placeholder={`Enter ${colKey.toLowerCase()}`}
										on:keydown={(e) =>
											focusNextInput(
												e,
												rowIndex,
												colIndex
											)}
										bind:value={tableData[rowIndex][colKey]}
										classes="search-border"
									/>
								</td>
							{/if}
						{/each}
						<td class="xs-td">
							<button
								class="delete"
								on:click={() => deleteRow(rowIndex)}
							>
								<img
									src="/assets/icon-trash.svg"
									alt="delete row"
								/></button
							>
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
		<div class="my-3">
			<CustomButton
				buttonText="Add 5 rows"
				on:click={addRow}
				onKeyDown={() => {}}
			/>
		</div>
	</div>
</LoaderContainer>

<style>
	table {
		width: 100%;
		border-collapse: collapse;
		max-height: 300px;
		overflow-y: auto;
	}

	th {
		text-align: left;
		font-weight: bold;
		color: #333;
	}

	td {
		vertical-align: middle;
		width: 200px;
	}

	td,
	th {
		border: 1px solid #ccc;
	}

	input,
	select {
		width: 100%;
		padding: 8px;
		font-size: 14px;
		border: 1px solid #ccc;
		border-radius: 4px;
		background: #fff;
		outline: none;
	}

	input:focus,
	select:focus {
		border-color: #007bff;
		box-shadow: 0 0 4px rgba(0, 123, 255, 0.25);
	}

	button.delete {
		background: none;
		border: none;
		color: #dc3545;
		cursor: pointer;
		font-size: 18px;
	}

	button.delete:hover {
		color: #a71d2a;
	}

	button.add {
		margin: 10px 0;
		padding: 10px 15px;
		background-color: #007bff;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
	}

	button.add:hover {
		background-color: #0056b3;
	}

	td {
		padding: 5px !important;
	}

	th {
		height: 52px !important;
		padding: 14px !important;
	}

	input {
		border-color: transparent !important;
	}

	select {
		border-color: transparent !important;
	}

	select:focus {
		box-shadow: none !important;
	}

	input:focus {
		box-shadow: none !important;
	}

	:global(.form-select.getgo.select.search-border) {
		border-color: transparent !important;
	}

	:global(.form-control.getgo.input.search-border) {
		border-color: transparent !important;
	}

	.med-td {
		width: 500px;
	}

	.sm-td {
		width: 212px;
	}

	.xs-td {
		width: 40px;
	}
</style>
